import classNames from 'classnames';
import type { LocationDescriptor } from 'history';
import { isUndefined } from 'lodash/fp';
import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { Feature } from '../../../../types/CompanyDtos';
import { CurrentUserDto } from '../../../../types/UserDtos';
import { useAppContext } from '../../hooks/useAppContext';
import { EventTrackingProps, trackEvent } from '../../services/Tracking';
import styles from './style.m.less';

export type Tab = {
    link: LocationDescriptor;
    label: React.ReactNode;
    visible: boolean;
    enabled?: boolean;
    active?: boolean;
    exact?: boolean;
    feature?: Feature;
    trackingLabel?: string;
    testId: string;
};

type Props = {
    tabs: Tab[];
    className?: string;
    trackingCategory?: EventTrackingProps['category'];
};

type TabProps = {
    tab: Tab;
    user: CurrentUserDto;
    trackingCategory?: EventTrackingProps['category'];
};

export default function NavigationTabs({ tabs, className, trackingCategory }: Props) {
    const appContext = useAppContext();
    return (
        <div className={classNames(styles.container, className)}>
            {tabs.map(tab => (
                <NavigationTab
                    tab={tab}
                    user={appContext.user}
                    key={typeof tab.link === 'string' ? tab.link : tab.link.pathname}
                    trackingCategory={trackingCategory}
                />
            ))}
        </div>
    );
}

function NavigationTab({ tab, user, trackingCategory }: TabProps) {
    const featureEnabled = !tab.feature || featureActive(tab.feature, user);
    const disabled = !(tab.enabled || tab.enabled === undefined);

    if (!tab.visible || (!featureEnabled && !isAnyAdmin(user))) {
        return null;
    }

    return (
        <NavLink
            to={tab.link}
            exact={tab.exact}
            className={classNames(styles.tabItem, {
                [styles.deactivated]: !featureEnabled,
                [styles.disabled]: disabled,
            })}
            isActive={isUndefined(tab.active) ? undefined : () => !!tab.active}
            activeClassName={styles.selected}
            tabIndex={disabled ? -1 : undefined}
            onClick={() => {
                if (trackingCategory) {
                    trackEvent({ category: trackingCategory, action: 'toggle tab', label: tab.trackingLabel });
                }
            }}
            data-testid={tab.testId}
        >
            {tab.label}
        </NavLink>
    );
}

function featureActive(feature: Feature, user: CurrentUserDto) {
    return user.company.enabledFeatures[feature];
}

function isAnyAdmin(user: CurrentUserDto) {
    const { isAdmin, isAssistant, isHR } = user;
    return isAdmin || isAssistant || isHR;
}
